// src/App.js
import React, { useEffect, useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import "./styles/tailwind.css";
import "./styles/wip-v2.css";
import 'react-grid-layout/css/styles.css';
// import 'react-resizable/css/styles.css'; // Using custom styles instead
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// --- React Query Imports ---
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// --- Layouts ---
import BaseLayout from './layouts/BaseLayout';

// --- Components ---
import DocumentHead from './components/DocumentHead';

// --- Contexts ---
import { AuthProvider, AuthContext } from "./context/AuthContext";
import AccessPreviewBanner from "./components/permissions/AccessPreviewBanner";
import { I18nProvider } from "./i18n";
import { SidebarProvider } from "./context/SidebarContext";
import { CustomerContextProvider } from "./context/CustomerContext";
import { ProductOrderStatsProvider } from "./context/ProductOrderStatsContext";
import { ProductDiscountProvider } from "./context/ProductDiscountContext";
import { TabsProvider } from "./context/TabsContext";

// --- Hooks ---
// Note: Cart is now handled locally via localStorage, no global cart query needed

// --- Utils ---
import ProtectedRoute from "./utils/ProtectedRoute";
import { getLastVisitedPage } from "./utils/sessionPersistence";
import performanceCleanup from "./utils/performanceCleanup";
import logger from "./utils/logger";

// --- Services ---
// Note: Cart services removed - now using local-first cart via CartManager and SyncService
import syncService from './services/SyncService';
import cartManager from './services/CartManager';
import activityService from './services/activityService';

// --- Pages ---
import LoginPage from "./pages/LoginPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import ProductsPageLazy from "./pages/ProductsPageLazy";

const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const OrdersPage = React.lazy(() => import("./pages/OrdersPage"));
const CartsManagementPage = React.lazy(() => import("./pages/CartsManagementPage"));
const OrderDetailPage = React.lazy(() => import("./pages/OrderDetailPage"));
const FulfillmentPage = React.lazy(() => import('./pages/FulfillmentPage'));
const FulfillmentDetailPage = React.lazy(() => import('./pages/FulfillmentDetailPage'));
const ProductsAdminPage = React.lazy(() => import("./pages/ProductsAdminPage"));
const ProductAdminDetailPage = React.lazy(() => import("./pages/ProductAdminDetailPage"));
const CustomersPage = React.lazy(() => import("./pages/CustomersPage"));
const CustomerDetailPage = React.lazy(() => import("./pages/CustomerDetailPage"));
const UserManagementPage = React.lazy(() => import("./pages/UserManagementPage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const AiConnectPage = React.lazy(() => import('./pages/AiConnectPage'));
const SalesAssignmentsPage = React.lazy(() => import("./pages/SalesAssignmentsPage"));
const CustomerManagerAssignmentsPage = React.lazy(() => import("./pages/CustomerManagerAssignmentsPage"));
const CustomerPermissionsPage = React.lazy(() => import("./pages/CustomerPermissionsPage"));
const EditProfile = React.lazy(() => import("./pages/EditProfile"));
const CartPage = React.lazy(() => import("./pages/CartPage"));
const CheckoutPage = React.lazy(() => import("./pages/CheckoutPage"));
const MapPage = React.lazy(() => import('./pages/MapPage'));
const LogisticsDashboard = React.lazy(() => import('./pages/LogisticsDashboard'));
const LogisticsRoutes = React.lazy(() => import('./pages/LogisticsRoutes'));
const LogisticsRouteDetail = React.lazy(() => import('./pages/LogisticsRouteDetail'));
const LogisticsTracking = React.lazy(() => import('./pages/LogisticsTracking'));
const LogisticsDrivers = React.lazy(() => import('./pages/LogisticsDrivers'));
const LogisticsDriverDetail = React.lazy(() => import('./pages/LogisticsDriverDetail'));
const LogisticsDeliveries = React.lazy(() => import('./pages/LogisticsDeliveries'));
const LogisticsDeliveryDetail = React.lazy(() => import('./pages/LogisticsDeliveryDetail'));
const LogisticsVehicles = React.lazy(() => import('./pages/LogisticsVehicles'));
const VehicleDetailPage = React.lazy(() => import('./pages/VehicleDetailPage'));
const LogisticsRoutePlanner = React.lazy(() => import('./pages/LogisticsRoutePlanner'));
const LogisticsLocationManagement = React.lazy(() => import('./pages/LogisticsLocationManagement'));
const LogisticsLocationDetail = React.lazy(() => import('./pages/LogisticsLocationDetail'));
const SystemSettingsPage = React.lazy(() => import('./pages/SystemSettingsPage'));
const InvoicesPage = React.lazy(() => import('./pages/InvoicesPage'));
const InvoiceDetailPage = React.lazy(() => import('./pages/InvoiceDetailPage'));
const CreditNotesPage = React.lazy(() => import('./pages/CreditNotesPage'));
const CreditNoteDetailPage = React.lazy(() => import('./pages/CreditNoteDetailPage'));
const NewCreditNotePage = React.lazy(() => import('./pages/NewCreditNotePage'));
const SystemLogsPage = React.lazy(() => import('./pages/SystemLogsPage'));
const CustomerAssignments = React.lazy(() => import('./pages/CustomerAssignments'));
const PermissionsDashboard = React.lazy(() => import('./pages/PermissionsDashboard'));
const PermissionControlPage = React.lazy(() => import('./pages/PermissionControlPage'));
const HeaderConfigPage = React.lazy(() => import('./pages/HeaderConfigPage'));
const SidebarConfigPage = React.lazy(() => import('./pages/SidebarConfigPage'));
const WarehousePage = React.lazy(() => import('./pages/WarehousePage'));
const WarehouseDetailPage = React.lazy(() => import('./pages/WarehouseDetailPage'));
const ZoneDetailPage = React.lazy(() => import('./pages/ZoneDetailPage'));
const PurchaseOrdersPage = React.lazy(() => import('./pages/PurchaseOrdersPage'));
const PurchasePlannerPage = React.lazy(() => import('./pages/PurchasePlannerPage'));
const SuppliersPage = React.lazy(() => import('./pages/SuppliersPage'));
const SupplierDetailPage = React.lazy(() => import('./pages/SupplierDetailPage'));
const ReceiptPage = React.lazy(() => import('./pages/ReceiptPage'));
const PickingPage = React.lazy(() => import('./pages/PickingPage'));
const PackingPage = React.lazy(() => import('./pages/PackingPage'));
const ReceiptDetailPage = React.lazy(() => import('./pages/ReceiptDetailPage'));
const PickingDetailPage = React.lazy(() => import('./pages/PickingDetailPage'));
const TerminalPickingDetailPage = React.lazy(() => import('./pages/TerminalPickingDetailPage'));
const PackingDetailPage = React.lazy(() => import('./pages/PackingDetailPage'));
const DeliveryNotePrint = React.lazy(() => import('./pages/DeliveryNotePrint'));
const PurchaseOrderDetailPage = React.lazy(() => import('./pages/PurchaseOrderDetailPage'));
const PurchaseInvoicesPage = React.lazy(() => import('./pages/PurchaseInvoicesPage'));
const PurchaseInvoiceDetailPage = React.lazy(() => import('./pages/PurchaseInvoiceDetailPage'));
const BillDetailPage = React.lazy(() => import('./pages/BillDetailPage'));
const DebitNotesPage = React.lazy(() => import('./pages/DebitNotesPage'));
const DebitNoteDetailPage = React.lazy(() => import('./pages/DebitNoteDetailPage'));
const NewDebitNotePage = React.lazy(() => import('./pages/NewDebitNotePage'));
const BatchDetailPage = React.lazy(() => import('./pages/BatchDetailPage'));
const InventoryManagementPage = React.lazy(() => import('./pages/InventoryManagementPage'));
const WarehouseTransfersPage = React.lazy(() => import('./pages/WarehouseTransfersPage'));
const WarehouseTransferDetailPage = React.lazy(() => import('./pages/WarehouseTransferDetailPage'));
const StockAdjustmentDetailPage = React.lazy(() => import('./pages/StockAdjustmentDetailPage'));
const CycleCountDetailPage = React.lazy(() => import('./pages/CycleCountDetailPage'));
const StocktakePage = React.lazy(() => import('./pages/StocktakePage'));
const StocktakeCountPage = React.lazy(() => import('./pages/StocktakeCountPage'));
const LocationDetailPage = React.lazy(() => import('./pages/LocationDetailPage'));
const ProductionDashboard = React.lazy(() => import('./pages/ProductionDashboard'));
const BOMManagement = React.lazy(() => import('./pages/BOMManagement'));
const ProductionOrders = React.lazy(() => import('./pages/ProductionOrders'));
const WorkCentres = React.lazy(() => import('./pages/WorkCentres'));
const ProductionPlanning = React.lazy(() => import('./pages/ProductionPlanning'));
const ProductionPlannerPage = React.lazy(() => import('./pages/ProductionPlannerPage'));
const WeeklyPlanPage = React.lazy(() => import('./pages/WeeklyPlanPage'));
const WeeklyPlanDetailPage = React.lazy(() => import('./pages/WeeklyPlanDetailPage'));
const ProductionTemplateDetailPage = React.lazy(() => import('./pages/ProductionTemplateDetailPage'));
const ProductionPlanningNew = React.lazy(() => import('./pages/ProductionPlanningNew'));
const ProductionReports = React.lazy(() => import('./pages/ProductionReports'));
const OperationsManagement = React.lazy(() => import('./pages/OperationsManagement'));
const SkillsManagement = React.lazy(() => import('./pages/SkillsManagement'));
const BOMDetailPage = React.lazy(() => import('./pages/BOMDetailPage'));
const BOMCreatePage = React.lazy(() => import('./pages/BOMCreatePage'));
const ProductionPlanDetailPage = React.lazy(() => import('./pages/ProductionPlanDetailPage'));
const ProductionPlanCreatePage = React.lazy(() => import('./pages/ProductionPlanCreatePage'));
const ProductionOrderDetailPage = React.lazy(() => import('./pages/ProductionOrderDetailPage'));
const BarcodesPage = React.lazy(() => import('./pages/BarcodesPage'));
const PriceManagementPage = React.lazy(() => import('./pages/PriceManagementPage'));
const PriceDocumentDetailPage = React.lazy(() => import('./pages/PriceDocumentDetailPage'));
const DiscountsPage = React.lazy(() => import('./pages/DiscountsPage'));
const DiscountDetailPage = React.lazy(() => import('./pages/DiscountDetailPage'));
const BannersPage = React.lazy(() => import('./pages/BannersPage'));
const BannerDetailPage = React.lazy(() => import('./pages/BannerDetailPage'));
const CatalogueGeneratorPage = React.lazy(() => import('./pages/CatalogueGeneratorPage'));
const CatalogueTemplateEditorPage = React.lazy(() => import('./pages/CatalogueTemplateEditorPage'));
const ProductionOrderCreatePage = React.lazy(() => import('./pages/ProductionOrderCreatePage'));
const WIPDetailV2Page = React.lazy(() => import('./pages/WIPDetailV2Page'));
const WIPTrackingV2 = React.lazy(() => import('./pages/WIPTrackingV2'));
const StationPage = React.lazy(() => import('./pages/StationPage'));
const WorkstationPage = React.lazy(() => import('./pages/WorkstationPage'));
const ProductionReleases = React.lazy(() => import('./pages/ProductionReleases'));
const ProductionReleaseDetail = React.lazy(() => import('./pages/ProductionReleaseDetail'));
const QRScanner = React.lazy(() => import('./pages/QRScanner'));
const CRMDashboard = React.lazy(() => import('./pages/CRMDashboard'));
const LeadsManagement = React.lazy(() => import('./pages/LeadsManagement'));
const LeadDetailPage = React.lazy(() => import('./pages/LeadDetailPage'));
const EventsLog = React.lazy(() => import('./pages/EventsLog'));
const EventLog = React.lazy(() => import('./pages/EventLog'));
const EventDetailPage = React.lazy(() => import('./pages/EventDetailPage'));
const CallLogPage = React.lazy(() => import('./pages/CallLogPage'));
const CRMEmailPage = React.lazy(() => import('./pages/CRMEmailPage'));
const OrganizerDashboard = React.lazy(() => import('./pages/OrganizerDashboard'));
const TaskManagement = React.lazy(() => import('./pages/TaskManagement'));
const ProjectManagement = React.lazy(() => import('./pages/ProjectManagement'));
const TaskCreate = React.lazy(() => import('./pages/TaskCreate'));
const ProjectCreate = React.lazy(() => import('./pages/ProjectCreate'));
const ProjectDetail = React.lazy(() => import('./pages/ProjectDetail'));
const ProjectEdit = React.lazy(() => import('./pages/ProjectEdit'));
const TaskDetail = React.lazy(() => import('./pages/TaskDetail'));
const TaskEdit = React.lazy(() => import('./pages/TaskEdit'));
const ReceivedDocumentsPage = React.lazy(() => import('./pages/ReceivedDocumentsPage'));
const ReceivedDocumentForm = React.lazy(() => import('./pages/ReceivedDocumentForm'));
const ReceivedDocumentDetail = React.lazy(() => import('./pages/ReceivedDocumentDetail'));
const DeliveryZonesPage = React.lazy(() => import('./pages/DeliveryZonesPage'));
const DeliveryZoneDetailPage = React.lazy(() => import('./pages/DeliveryZoneDetailPage'));
const ZoneNeighborsPage = React.lazy(() => import('./pages/ZoneNeighborsPage'));
const AssistantPage = React.lazy(() => import('./pages/AssistantPage'));
const AssistantSettingsPage = React.lazy(() => import('./pages/AssistantSettingsPage'));
const CashAccountsPage = React.lazy(() => import('./pages/cashmanagement/CashAccountsPage'));
const CashAccountDetailPage = React.lazy(() => import('./pages/cashmanagement/CashAccountDetailPage'));
const CashTransactionsPage = React.lazy(() => import('./pages/cashmanagement/CashTransactionsPage'));
const TransactionDetailPage = React.lazy(() => import('./pages/cashmanagement/TransactionDetailPage'));
const CustomerReceiptPage = React.lazy(() => import('./pages/cashmanagement/CustomerReceiptPage'));
const SupplierPaymentPage = React.lazy(() => import('./pages/cashmanagement/SupplierPaymentPage'));
const CustomerStatementPage = React.lazy(() => import('./pages/cashmanagement/CustomerStatementPage'));
const SupplierStatementPage = React.lazy(() => import('./pages/cashmanagement/SupplierStatementPage'));
const CashSettingsPage = React.lazy(() => import('./pages/cashmanagement/CashSettingsPage'));
const TransferPage = React.lazy(() => import('./pages/cashmanagement/TransferPage'));
const BankReconciliationPage = React.lazy(() => import('./pages/cashmanagement/BankReconciliationPage'));
const CashFlowReportPage = React.lazy(() => import('./pages/cashmanagement/CashFlowReportPage'));
const ScheduledPaymentsPage = React.lazy(() => import('./pages/cashmanagement/ScheduledPaymentsPage'));
const EmployeePaymentPage = React.lazy(() => import('./pages/cashmanagement/EmployeePaymentPage'));
const LoanPage = React.lazy(() => import('./pages/cashmanagement/LoanPage'));
const LoanDetailPage = React.lazy(() => import('./pages/cashmanagement/LoanDetailPage'));
const OpeningBalancesPage = React.lazy(() => import('./pages/cashmanagement/OpeningBalancesPage'));

// Accounting Pages
const ChartOfAccountsPage = React.lazy(() => import('./pages/accounting/ChartOfAccountsPage'));
const AccountDetailPage = React.lazy(() => import('./pages/accounting/AccountDetailPage'));
const CreateAccountPage = React.lazy(() => import('./pages/accounting/CreateAccountPage'));
const JournalEntriesPage = React.lazy(() => import('./pages/accounting/JournalEntriesPage'));
const JournalEntryDetailPage = React.lazy(() => import('./pages/accounting/JournalEntryDetailPage'));
const CreateJournalEntryPage = React.lazy(() => import('./pages/accounting/CreateJournalEntryPage'));
const FiscalPeriodsPage = React.lazy(() => import('./pages/accounting/FiscalPeriodsPage'));
const FinancialReportsPage = React.lazy(() => import('./pages/accounting/FinancialReportsPage'));
const GeneralLedgerPage = React.lazy(() => import('./pages/accounting/GeneralLedgerPage'));
const AccountingSettingsPage = React.lazy(() => import('./pages/accounting/AccountingSettingsPage'));
const AccountBalancesPage = React.lazy(() => import('./pages/accounting/AccountBalancesPage'));
const FixedAssetsPage = React.lazy(() => import('./pages/accounting/FixedAssetsPage'));
const FixedAssetDetailPage = React.lazy(() => import('./pages/accounting/FixedAssetDetailPage'));
const CreateFixedAssetPage = React.lazy(() => import('./pages/accounting/CreateFixedAssetPage'));
const RunDepreciationPage = React.lazy(() => import('./pages/accounting/RunDepreciationPage'));

// Payroll Pages
const PayrollDashboardPage = React.lazy(() => import('./pages/payroll/PayrollDashboardPage'));
const PayrollEmployeesPage = React.lazy(() => import('./pages/payroll/EmployeesPage'));
const PayrollEmployeeDetailPage = React.lazy(() => import('./pages/payroll/EmployeeDetailPage'));
const PayPeriodsPage = React.lazy(() => import('./pages/payroll/PayPeriodsPage'));
const PayPeriodDetailPage = React.lazy(() => import('./pages/payroll/PayPeriodDetailPage'));
const PayrollEntriesPage = React.lazy(() => import('./pages/payroll/PayrollEntriesPage'));
const PayrollEntryDetailPage = React.lazy(() => import('./pages/payroll/PayrollEntryDetailPage'));
const PayScenariosPage = React.lazy(() => import('./pages/payroll/PayScenariosPage'));
const TimesheetsPage = React.lazy(() => import('./pages/payroll/TimesheetsPage'));
const PayrollSchedulesPage = React.lazy(() => import('./pages/payroll/PayrollSchedulesPage'));

// Sales Targets Pages
const SalesTargetsPage = React.lazy(() => import('./pages/SalesTargetsPage'));
const SalesTargetDetailPage = React.lazy(() => import('./pages/SalesTargetDetailPage'));
const SalesTargetFormPage = React.lazy(() => import('./pages/SalesTargetFormPage'));
const RemunerationRulesPage = React.lazy(() => import('./pages/RemunerationRulesPage'));
const PenalizationRulesPage = React.lazy(() => import('./pages/PenalizationRulesPage'));
const RemunerationCalculationsPage = React.lazy(() => import('./pages/RemunerationCalculationsPage'));
const PaymentTermsPage = React.lazy(() => import('./pages/PaymentTermsPage'));
const CustomerSegmentationSettingsPage = React.lazy(() => import('./pages/CustomerSegmentationSettingsPage'));
const AnalyticsHubPage = React.lazy(() => import('./pages/AnalyticsHubPage'));
const ReportBuilderPage = React.lazy(() => import('./pages/ReportBuilderPage'));
const VelocityConfigPage = React.lazy(() => import('./pages/VelocityConfigPage'));
const HelpCentrePage = React.lazy(() => import('./pages/HelpCentrePage'));
const WebsitePagesPage = React.lazy(() => import('./pages/WebsitePagesPage'));
const WebsiteChromePage = React.lazy(() => import('./pages/WebsiteChromePage'));
const WebsiteMenusPage = React.lazy(() => import('./pages/WebsiteMenusPage'));
const WebsiteSettingsPage = React.lazy(() => import('./pages/WebsiteSettingsPage'));
const WebsitePageEditorPage = React.lazy(() => import('./pages/WebsitePageEditorPage'));
const PublicSitePage = React.lazy(() => import('./pages/PublicSitePage'));
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'));
const AlertsPage = React.lazy(() => import('./pages/AlertsPage'));
const ScraperConfigPage = React.lazy(() => import('./pages/ScraperConfigPage'));
const ScrapedProductsPage = React.lazy(() => import('./pages/ScrapedProductsPage'));

// Sends the user to their last visited page when they can still open it, otherwise to their group's home page
const HomeRedirect = ({ remember = false }) => {
  const { isAuthenticated, loading, accessLoaded, homePath, hasPagePermission } = useContext(AuthContext);
  if (isAuthenticated && (loading || !accessLoaded)) return null;

  const lastPage = remember && isAuthenticated ? getLastVisitedPage() : null;
  const restrictedPages = ['/', '/login', '/unauthorized'];
  const useLastPage = lastPage && !restrictedPages.includes(lastPage) && hasPagePermission(lastPage);

  return <Navigate to={useLastPage ? lastPage : homePath} replace />;
};

// The classic WIP pages are retired; old bookmarks, printed QR labels and
// stored alert links still resolve, landing on V2 with the query intact.
const LegacyWipRedirect = () => {
  const { wipId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/production/wip-v2/${wipId}${search}`} replace />;
};

const LegacyAccountRedirect = () => {
  const { accountId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/accounting/chart-of-accounts/${accountId}${search}`} replace />;
};

const LegacyFulfillmentRedirect = () => {
  const { fulfillmentId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/fulfillment/${fulfillmentId}${search}`} replace />;
};

const LegacyWarehousingRedirect = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/warehousing/, '/warehouses')}${search}`} replace />;
};

const LegacyJournalEntryCreateRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/accounting/journal-entries/new${search}`} replace />;
};

// --- AppContent Component (Handles Layout and Routing) ---
function AppContent() {
  // Get auth context and React Query client
  const { isAuthenticated } = useContext(AuthContext);
  
  
  // Note: Old cart service initialization removed - now using local-first cart
  // Cart is initialized per-component via useCart hook and CartManager singleton
  
  useEffect(() => {
    performanceCleanup.start(30000);
    cartManager.init();

    const handleCartAuthChange = (event) => {
      const { action, userId } = event.detail;
      logger.log('[App] Cart auth event received:', action);

      if (action === 'login' && userId) {
        logger.log('[App] User logged in, syncing cart for user:', userId);
        syncService.handleLogin(userId);
      }
      // Note: logout is now handled directly in AuthContext before token cleanup
      // to ensure cart sync completes with valid credentials
    };

    window.addEventListener('cart-auth-changed', handleCartAuthChange);

    if (isAuthenticated) {
      logger.log('[App] User already authenticated, starting sync service for background saves');
      syncService.start();
    }

    return () => {
      window.removeEventListener('cart-auth-changed', handleCartAuthChange);
      performanceCleanup.stop();
      activityService.stop();
      syncService.cleanup();
    };
  }, [isAuthenticated]);
  
  return (
    <>
      {/* Document head management */}
      <DocumentHead />
      <AccessPreviewBanner />

      <React.Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div></div>}>
      <Routes>
        {/* Public CMS website — rendered without the ERP layout */}
        <Route path="/site" element={<PublicSitePage />} />
        <Route path="/site/:slug" element={<PublicSitePage />} />
        <Route path="/onboarding/:token" element={<OnboardingPage />} />

        {/* All routes use BaseLayout which now includes both sidebars */}
        <Route element={<BaseLayout />}>
        {/* === Public Routes === */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/profile/ai-connect" element={<AiConnectPage />} />
        <Route path="/" element={<HomeRedirect remember />} />
        <Route path="/products" element={<ProductsPageLazy />} />
        <Route path="/fulfillments/:fulfillmentId" element={<LegacyFulfillmentRedirect />} />
        <Route path="/warehousing/zones/:id" element={<LegacyWarehousingRedirect />} />
        <Route path="/warehousing/locations/:id" element={<LegacyWarehousingRedirect />} />

        {/* === Protected Routes === */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:orderId" element={<OrderDetailPage />} />
          <Route path="/carts-management" element={<CartsManagementPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:id" element={<CustomerDetailPage />} />
          <Route path="/products/admin" element={<ProductsAdminPage />} />
          <Route path="/products/admin/:productId" element={<ProductAdminDetailPage />} />
          <Route path="/barcodes" element={<BarcodesPage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/users" element={<UserManagementPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/permissions-dashboard" element={<PermissionsDashboard />} />
          <Route path="/permission-control" element={<PermissionControlPage />} />
          <Route path="/header-config" element={<HeaderConfigPage />} />
          <Route path="/sidebar-config" element={<SidebarConfigPage />} />
          <Route path="/settings" element={<SystemSettingsPage />} />
          <Route path="/logs" element={<DashboardPage />} />
          <Route path="/sales-assignments" element={<SalesAssignmentsPage />} />
          <Route path="/customer-manager-assignments" element={<CustomerManagerAssignmentsPage />} />
          <Route path="/customer-permissions" element={<CustomerPermissionsPage />} />
          <Route path="/customer-assignments" element={<CustomerAssignments />} />
          <Route path="/edit-profile" element={<EditProfile />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/fulfillment" element={<FulfillmentPage />} />
          <Route path="/fulfillment/:fulfillmentId" element={<FulfillmentDetailPage />} />
          <Route path="/logistics" element={<LogisticsDashboard />} />
          <Route path="/logistics/routes" element={<LogisticsRoutes />} />
          <Route path="/logistics/routes/:routeId" element={<LogisticsRouteDetail />} />
          <Route path="/logistics/tracking" element={<LogisticsTracking />} />
          <Route path="/logistics/drivers" element={<LogisticsDrivers />} />
          <Route path="/logistics/drivers/:driverId" element={<LogisticsDriverDetail />} />
          <Route path="/logistics/deliveries" element={<LogisticsDeliveries />} />
          <Route path="/logistics/deliveries/:deliveryId" element={<LogisticsDeliveryDetail />} />
          <Route path="/logistics/vehicles" element={<LogisticsVehicles />} />
          <Route path="/logistics/vehicles/:vehicleId" element={<VehicleDetailPage />} />
          <Route path="/logistics/route-planner" element={<LogisticsRoutePlanner />} />
          <Route path="/logistics/locations" element={<LogisticsLocationManagement />} />
          <Route path="/logistics/locations/:locationId" element={<LogisticsLocationDetail />} />
          
          {/* Delivery Zone Routes */}
          <Route path="/logistics/delivery-zones" element={<DeliveryZonesPage />} />
          <Route path="/logistics/delivery-zones/:id" element={<DeliveryZoneDetailPage />} />
          <Route path="/logistics/zone-neighbors" element={<ZoneNeighborsPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/invoices/:invoiceId" element={<InvoiceDetailPage />} />
          <Route path="/credit-notes" element={<CreditNotesPage />} />
          <Route path="/credit-notes/new" element={<NewCreditNotePage />} />
          <Route path="/credit-notes/:creditNoteId" element={<CreditNoteDetailPage />} />
          <Route path="/system-logs" element={<SystemLogsPage />} />
          <Route path="/warehouses" element={<WarehousePage />} />
          <Route path="/warehouses/:id" element={<WarehouseDetailPage />} />
          <Route path="/warehouses/zones/:id" element={<ZoneDetailPage />} />
          <Route path="/purchase-order" element={<PurchaseOrdersPage />} />
          <Route path="/purchase-order/:id" element={<PurchaseOrderDetailPage />} />
          <Route path="/purchase-planner" element={<PurchasePlannerPage />} />
          <Route path="/suppliers" element={<SuppliersPage />} />
          <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
          <Route path="/receipt" element={<ReceiptPage />} />
          <Route path="/receipt/:id" element={<ReceiptDetailPage />} />
          <Route path="/picking" element={<PickingPage />} />
          <Route path="/picking/:id" element={<PickingDetailPage />} />
          <Route path="/picking/:id/terminal" element={<TerminalPickingDetailPage />} />
          <Route path="/packing" element={<PackingPage />} />
          <Route path="/packing/:id" element={<PackingDetailPage />} />
          <Route path="/packing/delivery-note/print" element={<DeliveryNotePrint />} />
          <Route path="/purchase-invoices" element={<PurchaseInvoicesPage />} />
          <Route path="/purchase-invoices/bills/new" element={<BillDetailPage />} />
          <Route path="/purchase-invoices/bills/:id" element={<BillDetailPage />} />
          <Route path="/purchase-invoices/:id" element={<PurchaseInvoiceDetailPage />} />
          <Route path="/debit-notes" element={<DebitNotesPage />} />
          <Route path="/debit-notes/new" element={<NewDebitNotePage />} />
          <Route path="/debit-notes/:debitNoteId" element={<DebitNoteDetailPage />} />
          <Route path="/warehousing/batches" element={<Navigate to="/warehousing/inventory?tab=batches" replace />} />
          <Route path="/warehousing/batches/:id" element={<BatchDetailPage />} />
          <Route path="/warehousing/inventory" element={<InventoryManagementPage />} />
          <Route path="/warehousing/stock-levels" element={<Navigate to="/warehousing/inventory?tab=stock-levels" replace />} />
          <Route path="/warehousing/stock-movements" element={<Navigate to="/warehousing/inventory?tab=stock-movements" replace />} />
          <Route path="/warehousing/transfers" element={<WarehouseTransfersPage />} />
          <Route path="/warehousing/transfers/:id" element={<WarehouseTransferDetailPage />} />
          <Route path="/warehousing/stock-adjustments/:adjustmentId" element={<StockAdjustmentDetailPage />} />
          <Route path="/warehousing/cycle-counts/:countId" element={<CycleCountDetailPage />} />
          <Route path="/warehousing/stocktake" element={<StocktakePage />} />
          <Route path="/warehousing/stocktake/:stocktakeId" element={<StocktakePage />} />
          <Route path="/warehousing/stocktake/:stocktakeId/group/:countId" element={<StocktakeCountPage />} />
          <Route path="/warehouses/locations/:id" element={<LocationDetailPage />} />
          <Route path="/production" element={<Navigate to="/production/dashboard" replace />} />
          <Route path="/production/dashboard" element={<ProductionDashboard />} />
          <Route path="/production/bom" element={<BOMManagement />} />
          <Route path="/production/orders" element={<ProductionOrders />} />
          <Route path="/production/workcentres" element={<WorkCentres />} />
          <Route path="/production/planner" element={<ProductionPlannerPage />} />
          <Route path="/production/weekly-plan" element={<WeeklyPlanPage />} />
          <Route path="/production/weekly-plan/templates/:templateId" element={<ProductionTemplateDetailPage />} />
          <Route path="/production/weekly-plan/:planId" element={<WeeklyPlanDetailPage />} />
          <Route path="/production/planning" element={<ProductionPlanning />} />
          <Route path="/production/planning/new" element={<ProductionPlanningNew />} />
          <Route path="/production/planning/create" element={<ProductionPlanCreatePage />} />
          <Route path="/production/planning/:planId" element={<ProductionPlanDetailPage />} />
          <Route path="/production/orders/create" element={<ProductionOrderCreatePage />} />
          <Route path="/production/orders/:orderId" element={<ProductionOrderDetailPage />} />
          <Route path="/production/wip" element={<Navigate to="/production/wip-v2" replace />} />
          <Route path="/production/wip-v2" element={<WIPTrackingV2 />} />
          <Route path="/production/station" element={<StationPage />} />
          <Route path="/production/workstation" element={<WorkstationPage />} />
          <Route path="/production/wip/:wipId" element={<LegacyWipRedirect />} />
          <Route path="/production/wip-v2/:wipId" element={<WIPDetailV2Page />} />
          <Route path="/production/releases" element={<ProductionReleases />} />
          <Route path="/production/releases/:id" element={<ProductionReleaseDetail />} />
          <Route path="/production/qr-scanner" element={<QRScanner />} />
          <Route path="/production/reports" element={<ProductionReports />} />
          <Route path="/production/operations" element={<OperationsManagement />} />
          <Route path="/production/skills" element={<SkillsManagement />} />
          <Route path="/production/bom/create" element={<BOMCreatePage />} />
          <Route path="/production/bom/:bomId" element={<BOMDetailPage />} />

          {/* Pricing Routes */}
          <Route path="/prices" element={<PriceManagementPage />} />
          <Route path="/prices/documents/new" element={<PriceDocumentDetailPage />} />
          <Route path="/prices/documents/:id" element={<PriceDocumentDetailPage />} />
          <Route path="/discounts" element={<DiscountsPage />} />
          <Route path="/discounts/new" element={<DiscountDetailPage />} />
          <Route path="/discounts/:id" element={<DiscountDetailPage />} />

          {/* Banner Management */}
          <Route path="/banners" element={<BannersPage />} />
          <Route path="/banners/:bannerId" element={<BannerDetailPage />} />

          {/* Catalogue Generator */}
          <Route path="/catalogue-generator" element={<CatalogueGeneratorPage />} />
          <Route path="/catalogue-generator/templates/new" element={<CatalogueTemplateEditorPage />} />
          <Route path="/catalogue-generator/templates/:id" element={<CatalogueTemplateEditorPage />} />

          {/* Website (CMS) Routes */}
          <Route path="/cms/pages" element={<WebsitePagesPage />} />
          <Route path="/cms/pages/new" element={<WebsitePageEditorPage />} />
          <Route path="/cms/pages/:id" element={<WebsitePageEditorPage />} />
          <Route path="/cms/posts" element={<WebsitePagesPage pageType="post" />} />
          <Route path="/cms/posts/new" element={<WebsitePageEditorPage pageType="post" />} />
          <Route path="/cms/posts/:id" element={<WebsitePageEditorPage pageType="post" />} />
          <Route path="/cms/chrome" element={<WebsiteChromePage />} />
          <Route path="/cms/menus" element={<WebsiteMenusPage />} />
          <Route path="/cms/settings" element={<WebsiteSettingsPage />} />

          {/* CRM Routes */}
          <Route path="/crm" element={<CRMDashboard />} />
          <Route path="/crm/dashboard" element={<CRMDashboard />} />
          <Route path="/crm/leads" element={<LeadsManagement />} />
          <Route path="/crm/leads/:id" element={<LeadDetailPage />} />
          <Route path="/crm/events" element={<EventsLog />} />
          <Route path="/crm/calls" element={<CallLogPage />} />
          <Route path="/crm/events/log" element={<EventLog />} />
          <Route path="/crm/events/:id" element={<EventDetailPage />} />

          {/* Organizer Routes */}
          <Route path="/organizer/emails" element={<CRMEmailPage />} />
          <Route path="/organizer" element={<OrganizerDashboard />} />
          <Route path="/organizer/dashboard" element={<OrganizerDashboard />} />
          <Route path="/organizer/tasks" element={<TaskManagement />} />
          <Route path="/organizer/tasks/create" element={<TaskCreate />} />
          <Route path="/organizer/tasks/:id" element={<TaskDetail />} />
          <Route path="/organizer/tasks/:id/edit" element={<TaskEdit />} />
          <Route path="/organizer/documents" element={<ReceivedDocumentsPage />} />
          <Route path="/organizer/documents/create" element={<ReceivedDocumentForm />} />
          <Route path="/organizer/documents/:id" element={<ReceivedDocumentDetail />} />
          <Route path="/organizer/documents/:id/edit" element={<ReceivedDocumentForm />} />
          <Route path="/organizer/projects" element={<ProjectManagement />} />
          <Route path="/organizer/projects/create" element={<ProjectCreate />} />
          <Route path="/organizer/projects/:id" element={<ProjectDetail />} />
          <Route path="/organizer/projects/:id/edit" element={<ProjectEdit />} />

          {/* AI Assistant */}
          <Route path="/assistant" element={<AssistantPage />} />
          <Route path="/assistant/settings" element={<AssistantSettingsPage />} />

          {/* Cash Management Routes */}
          <Route path="/cashmanagement" element={<Navigate to="/cashmanagement/accounts" replace />} />
          <Route path="/cashmanagement/accounts" element={<CashAccountsPage />} />
          <Route path="/cashmanagement/accounts/:accountId" element={<CashAccountDetailPage />} />
          <Route path="/cashmanagement/transactions" element={<CashTransactionsPage />} />
          <Route path="/cashmanagement/transactions/:transactionId" element={<TransactionDetailPage />} />
          <Route path="/cashmanagement/loans" element={<LoanPage />} />
          <Route path="/cashmanagement/loans/:loanId" element={<LoanDetailPage />} />
          <Route path="/cashmanagement/transfers" element={<TransferPage />} />
          <Route path="/cashmanagement/customer-statement" element={<CustomerStatementPage />} />
          <Route path="/cashmanagement/customer-statement/:customerId" element={<CustomerStatementPage />} />
          <Route path="/cashmanagement/supplier-statement" element={<SupplierStatementPage />} />
          <Route path="/cashmanagement/supplier-statement/:supplierId" element={<SupplierStatementPage />} />
          <Route path="/cashmanagement/reconciliation" element={<BankReconciliationPage />} />
          <Route path="/cashmanagement/reports" element={<CashFlowReportPage />} />
          <Route path="/cashmanagement/scheduled-payments" element={<ScheduledPaymentsPage />} />
          <Route path="/cashmanagement/opening-balances" element={<OpeningBalancesPage />} />
          <Route path="/cashmanagement/receipts/customer" element={<CustomerReceiptPage />} />
          <Route path="/cashmanagement/payments/supplier" element={<SupplierPaymentPage />} />
          <Route path="/cashmanagement/payments/employee" element={<EmployeePaymentPage />} />
          <Route path="/cashmanagement/settings" element={<CashSettingsPage />} />

          {/* Accounting Routes */}
          <Route path="/accounting" element={<Navigate to="/accounting/chart-of-accounts" replace />} />
          <Route path="/accounting/chart-of-accounts" element={<ChartOfAccountsPage />} />
          <Route path="/accounting/chart-of-accounts/new" element={<CreateAccountPage />} />
          <Route path="/accounting/chart-of-accounts/:accountId" element={<AccountDetailPage />} />
          <Route path="/accounting/accounts/:accountId" element={<LegacyAccountRedirect />} />
          <Route path="/accounting/account-balances" element={<AccountBalancesPage />} />
          <Route path="/accounting/journal-entries" element={<JournalEntriesPage />} />
          <Route path="/accounting/journal-entries/new" element={<CreateJournalEntryPage />} />
          <Route path="/accounting/journal-entries/:entryId" element={<JournalEntryDetailPage />} />
          <Route path="/accounting/create-journal-entry" element={<LegacyJournalEntryCreateRedirect />} />
          <Route path="/accounting/fiscal-periods" element={<FiscalPeriodsPage />} />
          <Route path="/accounting/reports" element={<FinancialReportsPage />} />
          <Route path="/accounting/general-ledger" element={<GeneralLedgerPage />} />
          <Route path="/accounting/settings" element={<AccountingSettingsPage />} />
          <Route path="/accounting/fixed-assets" element={<FixedAssetsPage />} />
          <Route path="/accounting/fixed-assets/new" element={<CreateFixedAssetPage />} />
          <Route path="/accounting/fixed-assets/depreciation" element={<RunDepreciationPage />} />
          <Route path="/accounting/fixed-assets/:id" element={<FixedAssetDetailPage />} />
          <Route path="/accounting/fixed-assets/:id/edit" element={<CreateFixedAssetPage />} />

          {/* Payroll Routes */}
          <Route path="/payroll" element={<Navigate to="/payroll/dashboard" replace />} />
          <Route path="/payroll/dashboard" element={<PayrollDashboardPage />} />
          <Route path="/payroll/employees" element={<PayrollEmployeesPage />} />
          <Route path="/payroll/employees/new" element={<PayrollEmployeeDetailPage />} />
          <Route path="/payroll/employees/:id" element={<PayrollEmployeeDetailPage />} />
          <Route path="/payroll/employees/:id/edit" element={<PayrollEmployeeDetailPage />} />
          <Route path="/payroll/pay-periods" element={<PayPeriodsPage />} />
          <Route path="/payroll/pay-periods/:id" element={<PayPeriodDetailPage />} />
          <Route path="/payroll/entries" element={<PayrollEntriesPage />} />
          <Route path="/payroll/entries/:id" element={<PayrollEntryDetailPage />} />
          <Route path="/payroll/scenarios" element={<PayScenariosPage />} />
          <Route path="/payroll/timesheets" element={<TimesheetsPage />} />
          <Route path="/payroll/schedules" element={<PayrollSchedulesPage />} />

          {/* Sales Targets Routes */}
          <Route path="/sales-targets" element={<SalesTargetsPage />} />
          <Route path="/sales-targets/new" element={<SalesTargetFormPage />} />
          <Route path="/sales-targets/remuneration-rules" element={<RemunerationRulesPage />} />
          <Route path="/sales-targets/penalization-rules" element={<PenalizationRulesPage />} />
          <Route path="/sales-targets/calculations" element={<RemunerationCalculationsPage />} />
          <Route path="/sales-targets/:id" element={<SalesTargetDetailPage />} />
          <Route path="/sales-targets/:id/edit" element={<SalesTargetFormPage />} />

          {/* Payment Terms */}
          <Route path="/payment-terms" element={<PaymentTermsPage />} />

          {/* Customer Segmentation Settings */}
          <Route path="/customer-segmentation-settings" element={<CustomerSegmentationSettingsPage />} />

          {/* Analytics Routes */}
          <Route path="/analytics" element={<AnalyticsHubPage />} />
          <Route path="/analytics/:department" element={<AnalyticsHubPage />} />
          <Route path="/analytics/:department/builder" element={<ReportBuilderPage />} />
          <Route path="/analytics/:department/builder/:id" element={<ReportBuilderPage />} />
          <Route path="/analytics/velocity-config" element={<VelocityConfigPage />} />

          {/* Website Scraper */}
          <Route path="/website-scraper" element={<ScraperConfigPage />} />
          <Route path="/scraped-products" element={<ScrapedProductsPage />} />

          {/* System Alerts */}
          <Route path="/alerts" element={<AlertsPage />} />

          {/* Help Centre */}
          <Route path="/help-centre" element={<HelpCentrePage />} />
        </Route>

        {/* Fallback for any other path */}
        <Route path="*" element={<HomeRedirect />} />
      </Route>
    </Routes>
    </React.Suspense>
    </>
  );
}

// --- React Query Client Setup ---
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      gcTime: 1000 * 60 * 2,
      refetchOnWindowFocus: false,
      retry: 1,
      refetchOnReconnect: false,
    },
    mutations: {
      gcTime: 1000 * 30
    },
  },
});

window.queryClient = queryClient;

// --- Main App Component ---
function App() {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      import("./utils/memoryDebug").then(() => {
        logger.log('[App] Memory debug monitoring enabled');
      });
    }
  }, []);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      toast.info(event.detail?.message || 'Your session has expired. Please log in again.', {
        position: 'top-center',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
      });
    };

    window.addEventListener('sessionExpired', handleSessionExpired);
    return () => window.removeEventListener('sessionExpired', handleSessionExpired);
  }, []);

  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const cache = queryClient.getQueryCache();
      const queries = cache.getAll();
      const inactive = queries.filter(q => q.getObserversCount() === 0);

      if (inactive.length > 10) {
        console.warn(`[QueryCache] Clearing ${inactive.length} inactive queries`);
        inactive.forEach(q => cache.remove(q));
      }

      if (queries.length > 100) {
        console.error(`[QueryCache] TOO MANY QUERIES (${queries.length}), clearing all`);
        queryClient.clear();
      }
    }, 120000);

    return () => clearInterval(cleanupInterval);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <I18nProvider>
          <CustomerContextProvider>
            <ProductOrderStatsProvider>
              <ProductDiscountProvider>
                <SidebarProvider>
                  <Router>
                    <TabsProvider>
                      <AppContent />
                    </TabsProvider>
                  </Router>
                </SidebarProvider>
              </ProductDiscountProvider>
            </ProductOrderStatsProvider>
          </CustomerContextProvider>
        </I18nProvider>
      </AuthProvider>
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom" />
      )}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </QueryClientProvider>
  );
}

export default App;
